/* eslint-disable @typescript-eslint/no-explicit-any */
import { Component, input, OnInit, output, ChangeDetectionStrategy } from '@angular/core';
import { DatePipe } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import {
  calculateLunchFee,
  formatScheduleDays,
  getLunchResponsibility,
  LunchConfiguration,
  Module,
  School,
  Student,
  Weekday,
} from '@awo-care/shared-core';
import { Timestamp } from 'firebase/firestore';

@Component({
  selector: 'app-registration-summary',
  imports: [DatePipe, MatCardModule, MatDividerModule, MatIconModule],
  templateUrl: 'registration-summary.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['registration-summary.component.scss'],
})
export class RegistrationSummaryComponent implements OnInit {
  data = input.required<Student>();
  /**
   * Zeigt bei Schulen ohne AWO-Essen den Zuständigkeitshinweis statt „Ja/Nein"
   * (#748). Nur aktivieren, wenn der Schul-Stand in `startInfo.school` frisch
   * ist (Anmelde-Stepper) — alte Snapshots ohne `lunchConfigurations` würden
   * AWO-Schulen sonst fälschlich als extern einstufen.
   */
  classifyLunchResponsibility = input(false);
  editStep = output<number>();

  formatScheduleDays = formatScheduleDays;

  // Localized strings
  readonly yes = $localize`:@@registration-summary.yes:Ja`;
  readonly no = $localize`:@@registration-summary.no:Nein`;

  ngOnInit(): void {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  calculateTotal(): number {
    const baseAmount = this.data().startInfo?.module?.fee?.amount ?? 0;
    if (this.showLunchNotice()) return baseAmount;
    const lunchAmount = this.data().startInfo?.includeLunch ? this.lunchFee() : 0;
    return baseAmount + lunchAmount;
  }

  /** Aufgelöste flexible Lunch-Config aus dem Schul-Snapshot, falls vorhanden. */
  private selectedLunchConfig(): LunchConfiguration | null {
    const id = this.data().startInfo?.lunchConfigurationId;
    const configs = (this.data().startInfo?.school as School | undefined)
      ?.lunchConfigurations;
    return id && configs ? configs.find((c) => c.id === id) ?? null : null;
  }

  /**
   * Monatlicher Essenspreis: flexible Konfiguration (inkl. Tagesauswahl) vor
   * Legacy-`startInfo.lunch`.
   */
  lunchFee(): number {
    const config = this.selectedLunchConfig();
    if (config && config.type !== 'EXTERNAL') {
      return calculateLunchFee(
        config,
        (this.data().startInfo?.selectedLunchDays ?? []) as Weekday[],
      );
    }
    return this.data().startInfo?.lunch?.fee?.amount ?? 0;
  }

  showLunchNotice(): boolean {
    if (!this.classifyLunchResponsibility()) return false;
    const school = this.data().startInfo?.school as School | undefined;
    const module = this.data().startInfo?.module as Module | undefined;
    if (!school || !module) return false;
    const config = this.selectedLunchConfig();
    if (config && config.type !== 'EXTERNAL') return false;
    if (!config && this.data().startInfo?.lunch) return false;
    return getLunchResponsibility(school, module) === 'EXTERNAL';
  }

  get contractDeadline(): Date | null {
    return this.formatDate(this.data().registration?.contractReturnDeadline);
  }

  get isDeadlineOverdue(): boolean {
    const d = this.contractDeadline;
    return !!d && !this.data().contract && d.getTime() < Date.now();
  }

  formatDate(date: Date | Timestamp | any | undefined): Date | null {
    if (!date) return null;

    // If it's already a Date object
    if (date instanceof Date) return date;

    // If it's a Timestamp with toDate method
    if (date?.toDate && typeof date.toDate === 'function') {
      return date.toDate();
    }

    // If it's a date string or timestamp number, convert to Date
    if (typeof date === 'string' || typeof date === 'number') {
      return new Date(date);
    }

    // If we can't handle the date format, return null
    return null;
  }
}
